import { Box, CircularProgress, Tab, Tabs } from "@mui/material";
import { ReactElement, useContext, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import CollectionSyncModal from "../../components/collection-modal/collection-sync-modal";
import Filter from "../../components/filter/filter";
import LayoutSwitcher from "../../components/layout-switcher/layout-switcher";
import { Collection as GqlCollection } from "../../generated/gql/graphql";
import useCollection from "../../hooks/query/collection/use-collection";
// eslint-disable-next-line import/order
import useText from "../../hooks/use-text";

import "./collections.css";

import Navigation from "../../layout/navigation";
import CollectionAssetsProvider from "../../providers/collection-assets-provider";
import { SearchTypes } from "../../types/enums/search";
import { LocalizedStrings, SupportedLanguages } from "../../utils/lang";

import CollectionItems from "./parts/collection-items/collection-items";
import CollectionItemsLimitSelector from "./parts/collection-items/collection-items-limit-selector";
import Header from "./parts/header/header";
import LeftNavigation from "./parts/left-navigation/left-navigation";
import PaginationItems from "./parts/pagination/pagination-items";
import Search from "./parts/search/search";
import SyncLogs from "./parts/sync-logs/sync-logs";

// eslint-disable-next-line import/order
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";

const ASSETS_TAB = "assets";
const SYNCS_TAB = "syncs";

export default function Collection(): ReactElement {
  const { id } = useParams();

  const { strings } = useText(local);

  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === SYNCS_TAB ? SYNCS_TAB : ASSETS_TAB;

  const [collection, setCollection] = useState<GqlCollection>();
  const [collectionSyncModal, setCollectionSyncModal] =
    useState<boolean>(false);

  function setTab(value: string): void {
    const params = new URLSearchParams(searchParams);
    if (value === SYNCS_TAB) {
      params.set("tab", SYNCS_TAB);
    } else {
      params.delete("tab");
    }
    setSearchParams(params);
  }

  // ToDo(john): Fetch only users collection
  const { data, loading } = useCollection(id || "-1");

  useEffect(() => {
    if (!data) {
      return;
    }

    setCollection(data.collection);
  }, [data]);

  if (!collection) {
    return <p>Loading</p>;
  }

  return (
    <CollectionAssetsProvider collection={collection}>
      <Navigation classes="!pl-[345px]">
        <Search label={strings.search.label} type={SearchTypes.asset} />
        <CollectionItemsLimitSelector />
        <LayoutSwitcher />
      </Navigation>
      <div className="flex w-full flex-row gap-[25px]">
        <div className="basis-[320px] shrink-0">
          <LeftNavigation />
        </div>
        <div className="shadow rounded-[15px] p-50 bg-white gap-[20px] basis-[100%] flex-wrap flex content-start">
          <div className="flex space-x-1 cursor-pointer items-center">
            <ArrowBackIosIcon className="h-[16px] w-[16px]" />
            <Link to={`/collections`}>{strings.backLink.label}</Link>
          </div>
          <Header
            title={collection?.name || ""}
            collection={collection}
            onSync={setCollectionSyncModal}
          />
          <Tabs
            className="w-full"
            value={tab}
            onChange={(_, value: string) => setTab(value)}
          >
            <Tab
              className="capitalize"
              value={ASSETS_TAB}
              label={strings.tabs.assets}
            />
            <Tab
              className="capitalize"
              value={SYNCS_TAB}
              label={strings.tabs.syncs}
            />
          </Tabs>
          {tab === ASSETS_TAB ? (
            <>
              <Filter collection={collection} />
              {loading ? (
                <Box className="flex w-full justify-center">
                  <CircularProgress />
                </Box>
              ) : (
                <CollectionItems collectionId={collection.id} />
              )}
              <PaginationItems />
            </>
          ) : (
            <SyncLogs collectionId={collection.id} />
          )}
        </div>
      </div>
      {collectionSyncModal && id && (
        <CollectionSyncModal
          collectionId={id}
          onClose={() => setCollectionSyncModal(false)}
        />
      )}
    </CollectionAssetsProvider>
  );
}

type LocalSchema = {
  backLink: {
    label: string;
  };
  search: {
    label: string;
    placeholder: string;
  };
  tabs: {
    assets: string;
    syncs: string;
  };
};

const local: LocalizedStrings<LocalSchema, SupportedLanguages> = {
  default: {
    backLink: {
      label: "Collections",
    },
    search: {
      label: "Search in collection",
      placeholder: "Enter keyword",
    },
    tabs: {
      assets: "Assets",
      syncs: "Synchronizations",
    },
  },
};
